/**
 * Contexto global de clientes — estado compartilhado FORA do React.
 *
 * Por que existe: o interceptor do axios (`@/lib/api`) precisa enviar a
 * seleção ativa em TODAS as requisições, inclusive as disparadas fora de
 * componentes React. Um módulo puro evita dependência circular com o
 * ClientContext.
 *
 * REGRAS:
 *  - o contexto é SEMPRE por usuário (nunca compartilhado entre contas);
 *  - `null` = sem contexto (o cabeçalho não é enviado → comportamento
 *    anterior, compatível com quem não usa o filtro);
 *  - `[]` = seleção explícita de "nenhum cliente" (o backend devolve
 *    nenhum dado — nunca "todos").
 */

const STORAGE_PREFIX = "astral:selected-clients";

/** Marca o estado "todos os clientes autorizados" na preferência local. */
export const ALL_CLIENTS_FLAG = "__ALL_CLIENTS__";

/** Cabeçalho lido pelo backend (deps.CLIENT_CONTEXT_HEADER). */
export const CLIENT_CONTEXT_HEADER = "X-Astral-Client-Context";

let activeUserId = null;
let activeSelectedIds = null;

// ---------------------------------------------------------------------------
// PRONTIDÃO DO CONTEXTO (evita corrida na primeira carga)
// ---------------------------------------------------------------------------
// Os módulos montam e consultam assim que a sessão existe — possivelmente
// ANTES de o ClientProvider resolver a seleção. Sem esta trava, a primeira
// consulta sairia sem o cabeçalho (e o backend devolveria dados de todos os
// clientes autorizados, não do contexto ativo).
//
// O interceptor aguarda `waitForClientContext()` antes de enviar. A promessa
// é resolvida quando o ClientProvider aplica a seleção (ou quando não há
// usuário) e tem TIMEOUT de segurança: uma requisição nunca fica presa.
const CONTEXT_READY_TIMEOUT_MS = 5000;
let contextReady = false;
let readyResolve = null;
let readyPromise = new Promise((resolve) => {
  readyResolve = resolve;
});

/** Marca o contexto como resolvido (chamado pelo ClientProvider). */
export function markClientContextReady() {
  if (contextReady) return;
  contextReady = true;
  if (readyResolve) readyResolve();
}

/** Volta ao estado "aguardando" (logout / troca de usuário). */
export function resetClientContextReady() {
  contextReady = false;
  readyPromise = new Promise((resolve) => {
    readyResolve = resolve;
  });
}

/**
 * Aguarda a primeira resolução do contexto (com timeout de segurança).
 * Nunca rejeita e nunca trava indefinidamente.
 */
export function waitForClientContext() {
  if (contextReady) return Promise.resolve();
  return Promise.race([
    readyPromise,
    new Promise((resolve) => setTimeout(resolve, CONTEXT_READY_TIMEOUT_MS)),
  ]);
}

/** Chave de armazenamento ISOLADA por usuário. */
export function storageKeyFor(userId) {
  return `${STORAGE_PREFIX}:${userId || "anon"}`;
}

/**
 * Define o contexto ativo (chamado pelo ClientContext a cada mudança).
 *
 * @param {string|null} userId  usuário autenticado (null no logout)
 * @param {string[]|null} selectedIds  seleção ativa; null = sem contexto
 */
export function setActiveClientContext(userId, selectedIds) {
  activeUserId = userId || null;
  if (selectedIds === null || selectedIds === undefined) {
    activeSelectedIds = null;
    return;
  }
  activeSelectedIds = Array.isArray(selectedIds)
    ? selectedIds.map((id) => String(id)).filter(Boolean)
    : null;
}

/** Contexto ativo no formato { userId, selectedIds }. */
export function getActiveClientContext() {
  return { userId: activeUserId, selectedIds: activeSelectedIds };
}

/**
 * Valor do cabeçalho a enviar.
 *
 * @returns {string|null} null = NÃO enviar (sem contexto ativo); caso
 * contrário, a lista separada por vírgula (string vazia = nenhum cliente).
 */
export function clientContextHeaderValue() {
  if (activeSelectedIds === null) return null;
  return activeSelectedIds.join(",");
}

/** Limpa o estado (logout). */
export function clearActiveClientContext() {
  activeUserId = null;
  activeSelectedIds = null;
}
